/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity */
import { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	getSuggestionProductKeyFromContentUrl,
	type Suggestion,
} from '../__types__/RemoteObject.ts';
import { getSuggestionReasoningType } from '../getSuggestionReasoning.ts';

import { type ReviewSuggestionsExperience } from './useReviewSuggestionsExperience.ts';

type UseReviewSuggestionsAnalyticsProps = {
	spaceId?: string;
	spaceKey?: string;
	orderedSuggestions: Suggestion[];
	titlesByContentId: Map<string, string>;
	selectedItems: Set<string>;
	rejectedItems: Set<string>;
	isLoading: boolean;
	isFetchingSuggestions: boolean;
	isSuppressed: boolean;
	isFilterApplied: boolean;
	filterKey: string[];
	modalOpenTimestamp?: number;
	reviewSuggestionsExperience: ReviewSuggestionsExperience;
};

export function useReviewSuggestionsAnalytics({
	spaceId,
	spaceKey,
	orderedSuggestions,
	titlesByContentId,
	selectedItems,
	rejectedItems,
	isLoading,
	isFetchingSuggestions,
	isSuppressed,
	isFilterApplied,
	filterKey,
	modalOpenTimestamp,
	reviewSuggestionsExperience,
}: UseReviewSuggestionsAnalyticsProps): {
	openTimestamp: number;
	fireDismissed: () => void;
	fireAccepted: () => void;
	fireSubmitFailed: () => void;
} {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const openTimestampRef = useRef(modalOpenTimestamp ?? Date.now());
	const dismissMethodRef = useRef<'close_button' | 'escape'>('close_button');

	const latestValuesRef = useRef({
		spaceId,
		spaceKey,
		orderedSuggestions,
		titlesByContentId,
		selectedItems,
		rejectedItems,
		createAnalyticsEvent,
	});
	latestValuesRef.current = {
		spaceId,
		spaceKey,
		orderedSuggestions,
		titlesByContentId,
		selectedItems,
		rejectedItems,
		createAnalyticsEvent,
	};

	const hasFiredScreenEventRef = useRef(false);
	useEffect(() => {
		if (hasFiredScreenEventRef.current) {
			return;
		}
		if (isFetchingSuggestions) {
			return;
		}
		hasFiredScreenEventRef.current = true;
		const {
			spaceId: si,
			spaceKey: sk,
			orderedSuggestions: suggestions,
			createAnalyticsEvent: cae,
		} = latestValuesRef.current;
		cae({
			type: 'sendScreenEvent',
			data: {
				name: 'curationModule',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					suggestionCount: suggestions.length,
				},
			},
		}).fire();
	}, [isFetchingSuggestions]);

	const renderedFilterKeyRef = useRef<string[] | null>(null);
	const observedFetchForKeyRef = useRef<string[] | null>(null);
	const hasConcludedExperienceRef = useRef(false);
	useEffect(() => {
		if (isFetchingSuggestions) {
			observedFetchForKeyRef.current = filterKey;
			return;
		}
		if (isLoading) {
			return;
		}
		if (renderedFilterKeyRef.current === filterKey) {
			return;
		}
		const isInitialRender = renderedFilterKeyRef.current === null;
		if (!isInitialRender && observedFetchForKeyRef.current !== filterKey) {
			return;
		}
		const {
			spaceId: si,
			spaceKey: sk,
			orderedSuggestions: suggestions,
			titlesByContentId: titles,
			createAnalyticsEvent: cae,
		} = latestValuesRef.current;
		const renderedSuggestions = suggestions.map((s, rankIndex) => ({
			suggestionId: s.id,
			suggestionUrl: s.url,
			sourceType: getSuggestionProductKeyFromContentUrl(s.url),
			providerId: s.providerId,
			rankIndex,
			suggestedParentId: s.parentId,
			reasoningType: getSuggestionReasoningType(s, titles),
			score: s.score,
			suggestionCreatedAt: s.createdAt,
		}));

		renderedFilterKeyRef.current = filterKey;
		cae({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'suggestions',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					modalLoadLatencyMs: Math.max(0, Date.now() - openTimestampRef.current),
					suggestions: renderedSuggestions,
					filterKey: isFilterApplied ? filterKey : undefined,
				},
			},
		}).fire();

		if (!hasConcludedExperienceRef.current) {
			if (isSuppressed) {
				hasConcludedExperienceRef.current = true;
				reviewSuggestionsExperience.abortNotProvisioned();
			} else if (renderedSuggestions.length > 0) {
				hasConcludedExperienceRef.current = true;
				reviewSuggestionsExperience.succeed();
			} else if (!isFilterApplied) {
				hasConcludedExperienceRef.current = true;
				reviewSuggestionsExperience.fail();
			}
		}
	}, [
		isFetchingSuggestions,
		isLoading,
		filterKey,
		isSuppressed,
		isFilterApplied,
		reviewSuggestionsExperience,
	]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				dismissMethodRef.current = 'escape';
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, []);

	const fireDismissed = useCallback((): void => {
		const {
			spaceId: si,
			spaceKey: sk,
			orderedSuggestions: suggestions,
			rejectedItems: rejected,
			createAnalyticsEvent: cae,
		} = latestValuesRef.current;
		const rejectedSuggestions = suggestions
			.filter((s) => rejected.has(s.id))
			.map((s) => ({
				suggestionId: s.id,
				suggestionUrl: s.url,
				sourceType: getSuggestionProductKeyFromContentUrl(s.url),
				providerId: s.providerId,
				suggestionCreatedAt: s.createdAt,
			}));
		cae({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'curationModule',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					suggestionCountAtDismiss: suggestions.length,
					dismissMethod: dismissMethodRef.current,
					timeInModuleMs: Date.now() - openTimestampRef.current,
					rejectedItemCount: rejected.size,
					rejectedSuggestions,
				},
			},
		}).fire();
		dismissMethodRef.current = 'close_button';
	}, []);

	const fireAccepted = useCallback((): void => {
		const {
			spaceId: si,
			spaceKey: sk,
			orderedSuggestions: suggestions,
			titlesByContentId: titles,
			selectedItems: selected,
			rejectedItems: rejected,
			createAnalyticsEvent: cae,
		} = latestValuesRef.current;
		const suggestionAcceptancePayload = (s: Suggestion) => ({
			suggestionId: s.id,
			suggestionUrl: s.url,
			sourceType: getSuggestionProductKeyFromContentUrl(s.url),
			providerId: s.providerId,
			targetFolderId: s.parentId,
			folderWasRecommended: true,
			reasoningType: getSuggestionReasoningType(s, titles),
			score: s.score,
			suggestionCreatedAt: s.createdAt,
		});
		const acceptedSuggestions = suggestions
			.filter((s) => selected.has(s.id))
			.map(suggestionAcceptancePayload);
		const dismissedSuggestions = suggestions
			.filter((s) => !selected.has(s.id) && !rejected.has(s.id))
			.map(suggestionAcceptancePayload);
		const rejectedSuggestions = suggestions
			.filter((s) => rejected.has(s.id))
			.map(suggestionAcceptancePayload);
		cae({
			type: 'sendTrackEvent',
			data: {
				action: 'accepted',
				actionSubject: 'suggestions',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					count: acceptedSuggestions.length,
					acceptedSuggestions,
					dismissedSuggestions,
					rejectedSuggestions,
				},
			},
		}).fire();

		acceptedSuggestions.forEach((s) => {
			cae({
				type: 'sendTrackEvent',
				data: {
					action: 'createdByFlow',
					actionSubject: 'content',
					objectType: 'embed',
					containerType: 'space',
					containerId: si,
					source: 'containerNavigation',
					attributes: {
						spaceId: si,
						spaceKey: sk,
						contentType: 'embed',
						flow: 'spaceCuration',
						suggestionId: s.suggestionId,
						sourceType: s.sourceType,
						providerId: s.providerId,
					},
				},
			}).fire();
		});
	}, []);

	const fireSubmitFailed = useCallback((): void => {
		const {
			spaceId: si,
			spaceKey: sk,
			orderedSuggestions: suggestions,
			selectedItems: selected,
			rejectedItems: rejected,
			createAnalyticsEvent: cae,
		} = latestValuesRef.current;
		const attemptedSuggestionPayload = (s: Suggestion) => ({
			suggestionId: s.id,
			suggestionUrl: s.url,
			sourceType: getSuggestionProductKeyFromContentUrl(s.url),
			providerId: s.providerId,
			suggestionCreatedAt: s.createdAt,
		});
		const acceptedSuggestions = suggestions
			.filter((s) => selected.has(s.id))
			.map(attemptedSuggestionPayload);
		const rejectedSuggestions = suggestions
			.filter((s) => rejected.has(s.id))
			.map(attemptedSuggestionPayload);
		cae({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'suggestions',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					acceptedCount: acceptedSuggestions.length,
					rejectedCount: rejectedSuggestions.length,
					acceptedSuggestions,
					rejectedSuggestions,
				},
			},
		}).fire();
	}, []);

	return { openTimestamp: openTimestampRef.current, fireDismissed, fireAccepted, fireSubmitFailed };
}

export function useCurationErrorStateAnalytics({
	spaceId,
	spaceKey,
	reason,
	openTimestamp,
}: {
	spaceId?: string;
	spaceKey?: string;
	reason: 'empty' | 'notProvisioned';
	openTimestamp: number;
}): void {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const hasFiredErrorEventRef = useRef(false);
	useEffect(() => {
		if (hasFiredErrorEventRef.current) {
			return;
		}
		hasFiredErrorEventRef.current = true;
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'curationModuleErrorState',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					reason,
					modalLoadLatencyMs: Math.max(0, Date.now() - openTimestamp),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, spaceId, spaceKey, reason, openTimestamp]);
}

export function useSuggestionClickAnalytics({
	spaceId,
	spaceKey,
}: {
	spaceId?: string;
	spaceKey?: string;
}): {
	fireSuggestionClicked: (suggestion: Suggestion) => void;
} {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const latestValuesRef = useRef({ spaceId, spaceKey, createAnalyticsEvent });
	latestValuesRef.current = { spaceId, spaceKey, createAnalyticsEvent };

	const fireSuggestionClicked = useCallback((suggestion: Suggestion): void => {
		const { spaceId: si, spaceKey: sk, createAnalyticsEvent: cae } = latestValuesRef.current;
		cae({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'suggestion',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					suggestionId: suggestion.id,
					suggestionUrl: suggestion.url,
					suggestedParentId: suggestion.parentId,
					sourceType: getSuggestionProductKeyFromContentUrl(suggestion.url),
					providerId: suggestion.providerId,
					score: suggestion.score,
					suggestionCreatedAt: suggestion.createdAt,
				},
			},
		}).fire();
	}, []);

	return { fireSuggestionClicked };
}

const SUGGESTION_PREVIEW_MIN_DWELL_MS = 500;

export function useSuggestionPreviewAnalytics({
	spaceId,
	spaceKey,
}: {
	spaceId?: string;
	spaceKey?: string;
}): {
	trackPreviewChange: (suggestion: Suggestion | null) => void;
	flushPreviewAnalytics: () => void;
} {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const latestValuesRef = useRef({ spaceId, spaceKey, createAnalyticsEvent });
	latestValuesRef.current = { spaceId, spaceKey, createAnalyticsEvent };

	const previewedSuggestionRef = useRef<Suggestion | null>(null);
	const previewStartRef = useRef<number | null>(null);

	const firePreviewed = useCallback((): void => {
		const suggestion = previewedSuggestionRef.current;
		const startedAt = previewStartRef.current;
		previewedSuggestionRef.current = null;
		previewStartRef.current = null;
		if (!suggestion || startedAt === null) {
			return;
		}
		const previewDwellMs = Date.now() - startedAt;
		if (previewDwellMs < SUGGESTION_PREVIEW_MIN_DWELL_MS) {
			return;
		}
		const { spaceId: si, spaceKey: sk, createAnalyticsEvent: cae } = latestValuesRef.current;
		cae({
			type: 'sendUIEvent',
			data: {
				action: 'previewed',
				actionSubject: 'suggestion',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					suggestionId: suggestion.id,
					suggestionUrl: suggestion.url,
					suggestedParentId: suggestion.parentId,
					sourceType: getSuggestionProductKeyFromContentUrl(suggestion.url),
					providerId: suggestion.providerId,
					suggestionCreatedAt: suggestion.createdAt,
					previewDwellMs,
				},
			},
		}).fire();
	}, []);

	const trackPreviewChange = useCallback(
		(suggestion: Suggestion | null): void => {
			if (previewedSuggestionRef.current?.id === suggestion?.id) {
				return;
			}
			firePreviewed();
			previewedSuggestionRef.current = suggestion;
			previewStartRef.current = suggestion ? Date.now() : null;
		},
		[firePreviewed],
	);

	const flushPreviewAnalytics = useCallback((): void => {
		firePreviewed();
	}, [firePreviewed]);

	useEffect(() => {
		return () => {
			firePreviewed();
		};
	}, [firePreviewed]);

	return { trackPreviewChange, flushPreviewAnalytics };
}
